import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📚 Wiki - Página de Boas-Vindas
 * 
 * Página inicial da Wiki explicando como funciona e com sumário das seções.
 */
const WelcomeWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:book-open-page-variant" />
          <span>Pagmus Wiki</span>
        </div>
        <p className={styles.heroText}>
          Central de documentação e ajuda da plataforma Pagmus.
          Aqui você encontra guias práticos sobre todas as funcionalidades do sistema.
        </p>
      </section>

      {/* Como Funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona</span>
          <p className={styles.sectionSubtitle}>
            Documentação voltada para produtores, com linguagem simples e exemplos práticos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={4}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:book-open-variant" className={styles.infoIcon} />
              <div>
                <span className={styles.infoTitle}>Guias Práticos</span>
                <p>
                  Cada seção contém guias passo a passo, exemplos visuais e
                  dicas práticas para usar as funcionalidades do sistema.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <span className={styles.infoTitle}>Perguntas Frequentes</span>
                <p>
                  FAQ completo em cada seção respondendo as dúvidas mais comuns
                  e situações do dia a dia.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:update" className={styles.infoIcon} />
              <div>
                <span className={styles.infoTitle}>Sempre Atualizada</span>
                <p>
                  A documentação é atualizada sempre que novas funcionalidades
                  são adicionadas ou melhorias são implementadas.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Sumário das Seções */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Seções Disponíveis</span>
          <p className={styles.sectionSubtitle}>
            Navegue pelas seções usando o menu lateral ou clique nos cards abaixo
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={styles.cardIcon}>
                <Icon icon="mdi:account-group" />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge}>Controle de Acesso</div>
                <span className={styles.cardTitle}>Acessos</span>
                <p>
                  Documentação completa sobre grupos de permissões, criação de usuários
                  (Colaboradores, Vendedores, Afiliados Diretos) e blocos modulares de acesso.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Tipos de usuários explicados</li>
                  <li><Icon icon="mdi:check-circle" /> Como criar grupos de permissões</li>
                  <li><Icon icon="mdi:check-circle" /> Blocos modulares de acesso</li>
                  <li><Icon icon="mdi:check-circle" /> Dicas e boas práticas</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Rede de Vendas</div>
                <span className={styles.cardTitle}>Afiliados e Vendedores</span>
                <p>
                  Guia completo sobre como gerenciar sua rede de vendas: criar vendedores,
                  convidar afiliados, atribuir produtos e configurar comissões.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Como criar vendedores e afiliados</li>
                  <li><Icon icon="mdi:check-circle" /> Atribuir produtos manualmente</li>
                  <li><Icon icon="mdi:check-circle" /> Aprovar e editar comissões</li>
                  <li><Icon icon="mdi:check-circle" /> Diferenças entre tipos</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Distribuição de Valores</div>
                <span className={styles.cardTitle}>Comissões</span>
                <p>
                  Entenda como funciona o sistema de comissões: quem recebe,
                  como calcular, e como configurar valores para cada participante.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Todos os participantes explicados</li>
                  <li><Icon icon="mdi:check-circle" /> Como funciona o cálculo</li>
                  <li><Icon icon="mdi:check-circle" /> Como configurar comissões</li>
                  <li><Icon icon="mdi:check-circle" /> Perguntas frequentes</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>

        <div className={styles.noteBox} style={{ marginTop: '3rem' }}>
          <Icon icon="mdi:information" />
          <span>
            Mais seções serão adicionadas conforme novas funcionalidades são implementadas.
            Sugestões e dúvidas podem ser enviadas via <strong>Tickets de Suporte</strong>.
          </span>
        </div>
      </section>

      {/* Dicas Rápidas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas Rápidas</span>
          <p className={styles.sectionSubtitle}>
            Algumas dicas para aproveitar melhor a Wiki
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
              <div>
                <span className={styles.tipTitle}>💡 Navegação Lateral</span>
                <p>
                  Use o <strong>menu lateral</strong> para navegar rapidamente entre as seções.
                  Cada seção possui seu próprio conteúdo e FAQ.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
              <div>
                <span className={styles.tipTitle}>💡 Busque por Tópicos</span>
                <p>
                  Cada seção está organizada por <strong>tópicos</strong> com explicações
                  detalhadas. Use o scroll para encontrar o que precisa.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
              <div>
                <span className={styles.tipTitle}>💡 Exemplos Práticos</span>
                <p>
                  Todas as seções contêm <strong>exemplos práticos</strong> e passo a passo
                  para facilitar o entendimento e uso das funcionalidades.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:lightbulb-on" className={styles.tipIcon} />
              <div>
                <span className={styles.tipTitle}>💡 FAQ em Cada Seção</span>
                <p>
                  No final de cada seção há um <strong>FAQ completo</strong> respondendo
                  as dúvidas mais frequentes sobre o tema.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Suporte */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Precisa de Ajuda?</span>
          <p className={styles.sectionSubtitle}>
            Se não encontrou o que precisa, entre em contato
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:headset" className={styles.infoIcon} />
          <div>
            <span className={styles.infoTitle}>Tickets de Suporte</span>
            <p>
              Acesse <strong>Ajuda → Tickets de Suporte</strong> para abrir um ticket
              com sua dúvida ou problema. Nossa equipe responderá o mais rápido possível.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelcomeWiki;

